// packages/console/src/components/panels/LiveRenditionPanel.tsx
//
// Rendition-parameters panel for the LIVE console, ported from the "Rendition"
// block of docs/design/ui_kits/aether-live/Transcoder.jsx. It edits the SELECTED
// LIVE preset (mode==="live"): rate control (CBR/VBR/CQ), target bitrate, GOP
// length, B-frames, and a quality preset p1..p7. Every edit PATCHes the live
// preset through the FT-3 orchestrator (onPatch); the derived spec rows below
// (keyframe interval = gop, latency mode) recompute from the controls.
//
// The prototype's illustrative fields map onto the real Preset model: CBR/VBR
// map to rateControl cbr/vbr, CQ maps to crf; the target slider drives
// bitrateKbps (Mb/s in the UI, kbps in the model); GOP length drives gopLength
// (frames); the p1..p7 chips drive speedPreset. There is no session data here,
// so this panel is fully populated the moment a live preset is selected and
// shows an empty state only when the workspace has no live preset yet (R10).

import { Panel, Eb, TChip, DragSlider, Row } from "../Parts";
import type { Preset, RateControl, SpeedPreset } from "../../api/jobs";
import type { PresetPatch } from "../../api/jobs";

// RC_CHIPS maps the design's rate-control labels to the model's RateControl enum.
const RC_CHIPS: { label: string; rc: RateControl }[] = [
  { label: "CBR", rc: "cbr" },
  { label: "VBR", rc: "vbr" },
  { label: "CQ", rc: "crf" },
];

const PRESET_CHIPS: SpeedPreset[] = ["p1", "p3", "p4", "p5", "p7"];

export function LiveRenditionPanel({
  preset,
  onPatch,
}: {
  preset: Preset | null;
  onPatch: (patch: PresetPatch) => void;
}) {
  const rightTag = preset ? (
    <span style={{ font: "500 13px var(--font-mono)", color: "#fff" }}>{preset.name}</span>
  ) : undefined;

  return (
    <Panel title="Rendition" style={{ flex: 1, minHeight: 0 }} bodyStyle={{ overflowY: "auto", flex: 1 }} right={rightTag}>
      {!preset ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
          No live preset for this workspace yet. Create one to configure the ladder.
        </div>
      ) : (
        <>
          <Eb style={{ marginBottom: 11 }}>Rate control</Eb>
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {RC_CHIPS.map((c) => (
              <TChip key={c.rc} active={preset.rateControl === c.rc} onClick={() => onPatch({ rateControl: c.rc })}>
                {c.label}
              </TChip>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Target bitrate in Mb/s; the model stores kbps, so scale on write. */}
            <DragSlider
              label="Target bitrate"
              value={(preset.bitrateKbps ?? 0) / 1000}
              min={0.4}
              max={24}
              step={0.1}
              unit="Mb/s"
              onChange={(v) => onPatch({ bitrateKbps: Math.round(v * 1000) })}
            />
            <DragSlider
              label="GOP length"
              value={preset.gopLength}
              min={1}
              max={300}
              step={1}
              unit="frames"
              onChange={(v) => onPatch({ gopLength: Math.round(v) })}
            />
            <DragSlider
              label="B-frames"
              value={preset.bFrames ?? 0}
              min={0}
              max={4}
              step={1}
              unit=""
              onChange={(v) => onPatch({ bFrames: Math.round(v) })}
            />
          </div>
          <div style={{ height: 1, background: "var(--line)", margin: "16px 0 12px" }} />
          <Eb style={{ marginBottom: 11 }}>Quality preset</Eb>
          <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
            {PRESET_CHIPS.map((p) => (
              <TChip key={p} active={preset.speedPreset === p} onClick={() => onPatch({ speedPreset: p })}>
                {p}
              </TChip>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              font: "var(--t-micro)",
              color: "var(--fg4)",
              marginBottom: 16,
            }}
          >
            <span>Fastest</span>
            <span>Highest quality</span>
          </div>
          <div style={{ height: 1, background: "var(--line)", margin: "0 0 8px" }} />
          <Row label="Codec" value={preset.videoCodec.toUpperCase()} />
          <Row label="Keyframe" value={`${preset.gopLength} frames`} />
          <Row label="Rate control" value={preset.rateControl.toUpperCase()} />
          <Row label="Chroma" value={`4:2:0 · ${preset.bitDepth ?? 8}-bit`} />
        </>
      )}
    </Panel>
  );
}
